import { useState, useEffect, useCallback } from 'react';
import { animateFavoriteToggle, showFavoriteAddedToast } from '../utils/favoritesAnimation';

export const FAVORITES_KEY = 'productFavorites';
export const FAVORITES_CHANGE_EVENT = 'favorites:change';

export function normalizeProductId(productId) {
  if (productId == null || productId === '') return null;
  return String(productId);
}

export function getFavorites() {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeProductId).filter(Boolean);
  } catch (error) {
    console.error('Error al leer favoritos:', error);
    return [];
  }
}

export function saveFavorites(favorites) {
  const normalized = [...new Set(favorites.map(normalizeProductId).filter(Boolean))];
  try {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(normalized));
    window.dispatchEvent(new CustomEvent(FAVORITES_CHANGE_EVENT, { detail: { favorites: normalized } }));
  } catch (error) {
    console.error('Error al guardar favoritos:', error);
  }
  return normalized;
}

function subscribeFavorites(onChange) {
  const sync = () => onChange(getFavorites());
  window.addEventListener(FAVORITES_CHANGE_EVENT, sync);
  window.addEventListener('storage', sync);
  return () => {
    window.removeEventListener(FAVORITES_CHANGE_EVENT, sync);
    window.removeEventListener('storage', sync);
  };
}

export const useFavorites = () => {
  const [favorites, setFavorites] = useState(() => getFavorites());

  useEffect(() => subscribeFavorites(setFavorites), []);

  const addToFavorites = useCallback((productId, sourceEl = null, productName = '') => {
    const id = normalizeProductId(productId);
    if (!id) return;
    setFavorites((prev) => {
      if (prev.includes(id)) return prev;
      animateFavoriteToggle('add', sourceEl);
      if (productName) showFavoriteAddedToast(productName);
      return saveFavorites([...prev, id]);
    });
  }, []);

  const removeFromFavorites = useCallback((productId, sourceEl = null) => {
    const id = normalizeProductId(productId);
    if (!id) return;
    setFavorites((prev) => {
      if (!prev.includes(id)) return prev;
      animateFavoriteToggle('remove', sourceEl);
      return saveFavorites(prev.filter((x) => x !== id));
    });
  }, []);

  const toggleFavorite = useCallback((productId, sourceEl = null, productName = '') => {
    const id = normalizeProductId(productId);
    if (!id) return false;
    const isFav = favorites.includes(id);
    if (isFav) {
      removeFromFavorites(id, sourceEl);
      return false;
    }
    addToFavorites(id, sourceEl, productName);
    return true;
  }, [favorites, addToFavorites, removeFromFavorites]);

  const isFavorite = useCallback(
    (productId) => favorites.includes(normalizeProductId(productId)),
    [favorites],
  );

  const clearFavorites = useCallback(() => {
    saveFavorites([]);
    setFavorites([]);
  }, []);

  return {
    favorites,
    addToFavorites,
    removeFromFavorites,
    toggleFavorite,
    isFavorite,
    clearFavorites,
    favoritesCount: favorites.length,
  };
};

export const useProductFavorite = (productId, productName = '') => {
  const id = normalizeProductId(productId);
  const [isFav, setIsFav] = useState(() => (id ? getFavorites().includes(id) : false));

  useEffect(() => {
    if (!id) {
      setIsFav(false);
      return undefined;
    }
    const sync = () => setIsFav(getFavorites().includes(id));
    sync();
    const unsub = subscribeFavorites(sync);
    const onIconSync = () => sync();
    window.addEventListener('favorites:icon-sync', onIconSync);
    return () => {
      unsub();
      window.removeEventListener('favorites:icon-sync', onIconSync);
    };
  }, [id]);

  const toggleFavorite = useCallback((sourceEl = null) => {
    if (!id) return false;
    const favorites = getFavorites();
    const isFavorite = favorites.includes(id);
    if (isFavorite) {
      saveFavorites(favorites.filter((x) => x !== id));
      setIsFav(false);
      animateFavoriteToggle('remove', sourceEl);
      return false;
    }
    saveFavorites([...favorites, id]);
    setIsFav(true);
    animateFavoriteToggle('add', sourceEl);
    if (productName) showFavoriteAddedToast(productName);
    return true;
  }, [id, productName]);

  return {
    isFavorite: isFav,
    toggleFavorite,
  };
};
